import { useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { connect, useDispatch } from "react-redux";

import type { IconName } from "@similarweb/icons";
import { IconButton } from "@similarweb/ui-components";

import { showSuccessToast } from "app/actions/toast_actions";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { translate } from "app/filters/i18nFilter";
import { KeywordGroupEditorHelpers } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import { keywordGroupsQueryClient, keywordGroupsTransforms } from "app/services/keyword-groups";
import { useCreateOrUpdateKeywordGroup, useKeywordGroups } from "app/services/keyword-groups/hooks";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { allTrackers } from "app/services/track/track";
import {
    ETableSelectionNewGroupDropdownMode,
    TableSelectionNewGroupDropdown,
} from "pro-features/components/TableSelection/src/TableSelectionNewGroupDropdown";
import type { NavigatorType } from "single-spa/react-app/ProNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

interface IKeywordsAddToGroupDropdownProps {
    keywordToAdd: string;
    buttonIcon: IconName;
    services: {
        translate: (key: string, obj?: any, defaultValue?: string) => string;
        swNavigator: NavigatorType;
    };
    onDropdownToggle?: (isOpen: boolean) => void;
    showToast: (href, text, label) => void;
}

const KeywordsAddToGroupDropdown = (props: IKeywordsAddToGroupDropdownProps) => {
    const { state } = useCurrentStateAndParams();
    const dispatch = useDispatch();
    const { services, keywordToAdd, showToast, onDropdownToggle, buttonIcon } = props;
    const createOrUpdateKeywordGroup = useCreateOrUpdateKeywordGroup();
    const { data: keywordGroups } = useKeywordGroups();

    const dropdownRef = useRef<TableSelectionNewGroupDropdown>(null);
    const maxKeywordsInGroup = KeywordGroupEditorHelpers.getMaxGroupCount();

    const [selectedGroup, setSelectedGroup] = useState<string>(null);
    const [isSelectionValid, setIsSelectionValid] = useState<boolean>(true);
    const [selectionErrMessage, setSelectionErrMessage] = useState<string>(null);
    const [selectMode, setSelectMode] = useState<ETableSelectionNewGroupDropdownMode>(
        ETableSelectionNewGroupDropdownMode.GROUP_LIST,
    );
    const [isKeywordGroupEditorOpen, setIsKeywordGroupEditorOpen] = useState<boolean>(false);

    const getLinkToGroup = (name?: string): string => {
        const { country, duration } = services.swNavigator.getParams();

        return services.swNavigator.href("organicsearch_keywordAnalysis_overview_2", {
            country,
            duration,
            keyword: `*${name}`,
        });
    };

    const showGroupLinkToast = (name, text, label) => {
        const linkToGroup = getLinkToGroup(name);
        showToast(linkToGroup, text, label);
    };

    const onSubmit = async (name) => {
        const { isValid, errorMessage } = await KeywordGroupEditorHelpers.validateTitle(name);

        if (!isValid) {
            setIsSelectionValid(isValid);
            setSelectionErrMessage(errorMessage);
            return;
        }

        const groupToCreate = KeywordGroupEditorHelpers.keywordGroupFromList({
            title: name,
            items: [{ text: keywordToAdd }],
        });

        const newGroup = await createOrUpdateKeywordGroup.mutateAsync(groupToCreate);
        if (!newGroup) {
            dropdownRef?.current?.close();
            return;
        }

        showGroupLinkToast(
            newGroup.Id,
            services.translate("table.selection.keywords.groupcreated"),
            services.translate("table.selection.keywords.seegroup"),
        );

        setSelectMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
        setIsSelectionValid(true);
        setSelectionErrMessage(null);
        dropdownRef?.current?.close();
    };

    const onCancel = () => {
        setSelectMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
        setIsSelectionValid(true);
        setSelectionErrMessage(null);
    };

    const onNewGroupClick = () => {
        if (maxKeywordsInGroup < 1) {
            setSelectMode(ETableSelectionNewGroupDropdownMode.MAX_ITEMS);
            return;
        }

        setIsKeywordGroupEditorOpen(true);
    };

    const onExistingGroupClick = async (item) => {
        const currentGroup = await keywordGroupsQueryClient.getGroupById(item.id);
        setSelectedGroup(currentGroup.Name);

        const currentKeywords = currentGroup.Keywords.map((keyword: string) => {
            return { text: keyword };
        });

        const isKeywordExists = currentKeywords.some((groupKeyword) => groupKeyword.text === keywordToAdd);

        if (isKeywordExists) {
            setSelectMode(ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS);
            return;
        }

        const updatedGroup = KeywordGroupEditorHelpers.keywordGroupFromList(
            {
                title: currentGroup.Name,
                items: [...currentKeywords, { text: keywordToAdd }],
            },
            currentGroup,
        );

        if (updatedGroup.Keywords.length > maxKeywordsInGroup) {
            setSelectMode(ETableSelectionNewGroupDropdownMode.MAX_ITEMS);
            return;
        }

        const updatedGroupResult = await createOrUpdateKeywordGroup.mutateAsync(updatedGroup);
        if (!updatedGroupResult) {
            return;
        }

        setSelectMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
        setIsSelectionValid(true);
        setSelectionErrMessage(null);

        if (dropdownRef?.current) {
            dropdownRef.current.close();
        }

        showGroupLinkToast(
            currentGroup.Id,
            services.translate("table.selection.keywords.groupupdated", {
                count: 1,
            }),
            services.translate("table.selection.keywords.seegroup"),
        );
    };

    const onGroupClick = (item) => {
        if (!item.id) {
            onNewGroupClick();
            return;
        }

        onExistingGroupClick(item);
    };

    const showListSavedSuccessfullyToast = (savedList: IUserKeywordGroup) => {
        dispatch(showSuccessToast(translate("create.list.wizard.toast", { listName: savedList.Name })));
    };

    const refreshPageWithSavedList = (savedList: IUserKeywordGroup) => {
        proNavigator.go(
            state.name,
            {
                keyword: `*${savedList.Id}`,
            },
            { reload: true },
        );
    };

    const onSavedList = (savedList: IUserKeywordGroup) => {
        showListSavedSuccessfullyToast(savedList);
        refreshPageWithSavedList(savedList);
    };
    return (
        <>
            <TableSelectionNewGroupDropdown
                appendTo={"body"}
                renderDropdownButton={() => (
                    <IconButton
                        type="flat"
                        iconName={buttonIcon}
                    />
                )}
                ref={(ref) => {
                    dropdownRef.current = ref;
                }}
                groups={keywordGroupsTransforms.toDropDown(keywordGroups)}
                onSubmit={onSubmit}
                isLoading={false}
                error={!isSelectionValid}
                errorMessage={selectionErrMessage}
                onGroupClick={onGroupClick}
                mode={selectMode}
                currentGroup={selectedGroup}
                onCancel={onCancel}
                groupIconName="keyword-group"
                newGroupNameLabel={services.translate("table.selection.newgroup.keywords.title")}
                trackingCategory="add to keyword group"
                allItemsExistsMessage={services.translate("table.selection.keywords.exists")}
                maxItemsMessage={services.translate("table.selection.keywords.max.keywords", {
                    count: maxKeywordsInGroup ?? 0,
                })}
                newGroupItemText={services.translate("table.selection.newgroup.keywords.create")}
                onToggleDropdown={(isOpen) => {
                    if (onDropdownToggle) {
                        onDropdownToggle(isOpen);
                    }
                }}
                translate={services.translate}
            />
            <KeywordsGroupEditorModal
                onClose={() => {
                    setIsKeywordGroupEditorOpen(false);
                }}
                open={isKeywordGroupEditorOpen}
                seedKeyword={keywordToAdd}
                onSave={onSavedList}
            />
        </>
    );
};

const mapDispatchToProps = (dispatch) => {
    return {
        showToast: (href, text, label) => {
            dispatch(
                showSuccessToast(
                    getToastItemComponent({
                        text,
                        linkText: label,
                        href,
                        onClick: allTrackers.trackEvent.bind(
                            allTrackers,
                            "add to keyword group",
                            "click",
                            "internal link/keywordAnalysis-organic",
                        ),
                    }),
                ),
            );
        },
    };
};

export default connect(null, mapDispatchToProps)(KeywordsAddToGroupDropdown);
